import type { TypedSupabaseClient } from '../client';
import { runProPricePropagation } from './pro-price-propagation';
import type {
  IProArticlePricingRepository,
  ProArticlePricingRow,
  UpsertProArticlePricingInput,
} from '../../../interfaces/src';

const PRICING_FETCH_BATCH_SIZE = 1000;

export class SupabaseProArticlePricingRepository implements IProArticlePricingRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listForArticles(articleIds: string[]): Promise<ProArticlePricingRow[]> {
    if (articleIds.length === 0) return [];
    const { data, error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .select('*')
      .in('article_id', articleIds);
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapPricing);
  }

  async listAll(): Promise<ProArticlePricingRow[]> {
    const rows: Array<Record<string, unknown>> = [];
    let offset = 0;
    let total: number | null = null;

    do {
      const { data, error, count } = await (this.supabase as any)
        .from('pro_article_pricing')
        .select('*', { count: 'exact' })
        .range(offset, offset + PRICING_FETCH_BATCH_SIZE - 1);
      if (error) throw error;

      const batch = (data ?? []) as Array<Record<string, unknown>>;
      rows.push(...batch);
      total = typeof count === 'number' ? count : total;
      if (batch.length < PRICING_FETCH_BATCH_SIZE) break;
      offset += PRICING_FETCH_BATCH_SIZE;
    } while (total === null || offset < total);

    return rows.map(mapPricing);
  }

  async upsert(input: UpsertProArticlePricingInput): Promise<ProArticlePricingRow> {
    const { data, error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .upsert(toPricingPayload(input))
      .select()
      .single();
    if (error) throw error;
    // Les paliers Pro SONT le prix que voient les électriciens : les devis en
    // cours et la commission des commandes non expédiées suivent (idem delete,
    // qui fait retomber l'article au prix public).
    await runProPricePropagation(this.supabase, [input.articleId]);
    return mapPricing(data as Record<string, unknown>);
  }

  async upsertMany(inputs: UpsertProArticlePricingInput[]): Promise<ProArticlePricingRow[]> {
    if (inputs.length === 0) return [];

    // PostgREST écrit UN insert par requête : toutes les lignes d'un même appel
    // doivent porter les mêmes colonnes. On regroupe donc par « jeu de colonnes »
    // — sans quoi une ligne qui omet `name_fr` (pour le préserver) forcerait
    // l'ajout de la colonne à NULL sur tout le lot, effaçant les titres Pro.
    const groups = new Map<string, Array<Record<string, unknown>>>();
    for (const input of inputs) {
      const payload = toPricingPayload(input);
      const signature = Object.keys(payload).sort().join('|');
      const group = groups.get(signature);
      if (group) group.push(payload);
      else groups.set(signature, [payload]);
    }

    const rows: ProArticlePricingRow[] = [];
    for (const payloads of groups.values()) {
      const { data, error } = await (this.supabase as any)
        .from('pro_article_pricing')
        .upsert(payloads)
        .select();
      if (error) throw error;
      rows.push(...((data ?? []) as Array<Record<string, unknown>>).map(mapPricing));
    }

    // UNE seule propagation pour tout le lot.
    await runProPricePropagation(this.supabase, inputs.map((i) => i.articleId));
    return rows;
  }

  async delete(articleId: string): Promise<void> {
    await this.deleteMany([articleId]);
  }

  async deleteMany(articleIds: string[]): Promise<void> {
    const ids = [...new Set(articleIds.filter(Boolean))];
    if (ids.length === 0) return;
    const { error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .delete()
      .in('article_id', ids);
    if (error) throw error;
    await runProPricePropagation(this.supabase, ids);
  }
}

function toPricingPayload(input: UpsertProArticlePricingInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    article_id: input.articleId,
    prix_pro_min: input.prixProMin ?? null,
    prix_pro_moy: input.prixProMoy ?? null,
    prix_pro_max: input.prixProMax ?? null,
    remise_min: input.remiseMin ?? 0,
    remise_moy: input.remiseMoy ?? 0,
    remise_max: input.remiseMax ?? 0,
  };
  // The Pro title/description/reference are only touched when the caller
  // says so. `merge-duplicates` updates exactly the columns present in the
  // payload, so omitting them preserves what's stored — that's what keeps a
  // bulk discount edit from the products list (which knows nothing about the
  // presentation fields) from silently wiping a Pro title. An empty string
  // means "no Pro title" and is stored as NULL, so reads fall back to the
  // article instead of showing a blank label.
  if (input.nameFr !== undefined) payload['name_fr'] = input.nameFr?.trim() || null;
  if (input.descriptionFr !== undefined) {
    payload['description_fr'] = input.descriptionFr?.trim() || null;
  }
  if (input.reference !== undefined) payload['reference'] = input.reference?.trim() || null;
  return payload;
}

function mapPricing(row: Record<string, unknown>): ProArticlePricingRow {
  return {
    articleId: row['article_id'] as string,
    prixProMin: row['prix_pro_min'] != null ? Number(row['prix_pro_min']) : null,
    prixProMoy: row['prix_pro_moy'] != null ? Number(row['prix_pro_moy']) : null,
    prixProMax: row['prix_pro_max'] != null ? Number(row['prix_pro_max']) : null,
    remiseMin: Number(row['remise_min'] ?? 0),
    remiseMoy: Number(row['remise_moy'] ?? 0),
    remiseMax: Number(row['remise_max'] ?? 0),
    nameFr: (row['name_fr'] as string | null) ?? null,
    descriptionFr: (row['description_fr'] as string | null) ?? null,
    reference: (row['reference'] as string | null) ?? null,
    updatedAt: row['updated_at'] as string,
  };
}
